import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import type { ColumnDef } from '@tanstack/react-table';

import { formatPrice } from 'utils/price';
import { useGetSenseiBasketQuery, TransactionState } from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import { Loader } from 'components/Loader';
import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { CopyValue } from 'components/CopyValue';
import { DetailLink, DetailValue } from 'components/DetailsView';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { TotemLabel } from 'components/TotemLabel';
import { DataTable, dateColumn, textColumn } from 'components/DataTable';
import { StatusTag } from 'pages/Transactions/components/StatusTag';

type ParamTypes = {
    basketId: string;
};

type TransactionRow = {
    _id: string;
    createdAt: number;
    siteName: string;
    newState: TransactionState;
    productName: string;
    priceHT: number;
    tva: number;
};

const transactionColumns: ColumnDef<TransactionRow, unknown>[] = [
    dateColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'createdAt',
        header: 'Date',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'newState',
        header: 'Statut',
        defaultVisible: true,
        cell: ({ row }) => <StatusTag state={row.original.newState} />,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'siteName',
        header: 'Site',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'productName',
        header: 'Produit',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        id: 'price',
        accessorFn: (row) => formatPrice(row.priceHT * (1 + row.tva)),
        header: 'Montant',
        defaultVisible: true,
    }),
];

export const SenseiBasket = () => {
    const { basketId = '' } = useParams<ParamTypes>();

    const { data: senseiBasketData, loading, error } = useGetSenseiBasketQuery({ variables: { basketId } });
    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }
    if (error || !senseiBasketData?.senseiBasket) {
        throw new Error('Une erreur est survenue lors de la récupération du panier');
    }

    const { senseiBasket } = senseiBasketData;

    const {
        _id,
        createdAt,
        items,
        senseiBasketId,
        senseiColumnId,
        userId,
        basketCreationDate,
        storeEntryDate,
        storeExitDate,
        transactions,
    } = senseiBasket;

    const { userFullName } = transactions?.[0] || 'utilisateur inconnu - impossible';

    const sortedTransactions = [...transactions].sort(
        (transactionA, transactionB) => transactionB.createdAt - transactionA.createdAt,
    );
    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.senseiBasket} />
                </HeaderTitle>
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionContainer title="Informations générales" keepOpen>
                        <SectionColumn>
                            <CopyValue label="Sensei Basket ID" value={_id} />
                            <CopyValue label="Identifiant Sensei" value={senseiBasketId.toString()} />
                            <DetailLink
                                label="Utilisateur"
                                name={userFullName || 'inconnu'}
                                value={userId}
                                path="user"
                            />
                            <DetailValue label="Colonne" value={senseiColumnId || 'inconnue'} />
                            <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                            <DetailValue
                                label="Ouverture de la porte"
                                value={getExtendedFormattedDatetime(storeEntryDate)}
                            />
                            <DetailValue
                                label="Fermeture de la porte"
                                value={getExtendedFormattedDatetime(storeExitDate)}
                            />
                            <DetailValue
                                label="Creation du panier - Sensei"
                                value={getExtendedFormattedDatetime(basketCreationDate)}
                            />
                            <TotemLabel>Produits détectés</TotemLabel>
                            <TableContainer>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>sku (productId)</th>
                                            <th>quantité</th>
                                            <th>montant</th>
                                            <th>taxes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(({ quantity, sku, lineAmount, lineTax }, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <DetailLink
                                                        name={
                                                            (sku &&
                                                                transactions.find(({ productId }) => productId === sku)
                                                                    ?.productName) ||
                                                            sku ||
                                                            'produit inconnu'
                                                        }
                                                        value={sku}
                                                        path="product"
                                                    />
                                                </td>
                                                <td>{quantity}</td>
                                                <td>{lineAmount}</td>
                                                <td>{lineTax}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </TableContainer>
                            <TotemLabel>Transactions</TotemLabel>
                            <DataTable<TransactionRow>
                                columns={transactionColumns}
                                data={sortedTransactions}
                                filterMode="client"
                                getRowId={(row) => row._id}
                                getRowLink={(row) => `/transaction/${row._id}`}
                                autoFocusSearch={false}
                            />
                        </SectionColumn>
                    </SectionContainer>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 5px;
    }

    table {
        border-spacing: 0;
        border-collapse: collapse;
        color: ${({ theme }) => theme.textColor};

        th,
        td {
            text-align: left;
            border: 1px solid ${({ theme }) => theme.lightBorderColor};
            border-spacing: 1px;
        }

        th {
            padding: 10px;
        }

        td {
            padding: 5px;
        }
    }
`;
